import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { GameIcon } from "../assets/gameIcons.js";
import {
  initialBattleHealth,
  simulate,
  type BattleEvent,
  type BattleInput,
  type BattleResult,
} from "@zend/rules";
import { useSceneStore } from "../scene/SceneHost.js";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { battleReturnRoute } from "./return-route.js";
import { Button } from "../ui/Button.js";
import { battlesApi } from "../units/api.js";
import { tierColor, unitName } from "../units/shared.js";
import { unitById } from "@zend/content";
import type { BattleReport } from "../units/types.js";
import { BattleScene } from "./BattleScene.js";
import { ResultCard } from "./Results.js";
import { useReducedMotion } from "../units/useReducedMotion.js";
import { isUltimate } from "./presentation.js";
import { FloatingNumber, UltimateBanner, FLOAT_LIFETIME_MS } from "./BattleFeedback.js";
import "./battle-view.css";
import { TacticsPanel } from "./TacticsPanel.js";

interface Floating {
  id: number;
  actor: string;
  text: ReactNode;
  color: string;
  born: number;
}

interface HpState {
  hp: number;
  max: number;
  alive: boolean;
}

const TICKS_PER_SECOND = 10;

/**
 * Battle replay (R33): re-simulates the stored input with the shared rules, checks the hash, and plays the
 * event stream over the 3D arena with HP bars, floating numbers, ultimate banners, speed 1×/2×/4× and skip.
 */
export function BattleView({ battleId }: { battleId: string }) {
  const t = useT();
  const reducedMotion = useReducedMotion();
  const handle = useSceneStore((s) => s.handle);
  const [storedReport, setReport] = useState<BattleReport | null>(null);
  const [wave, setWave] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [speed, setSpeed] = useState(1);
  const [tick, setTick] = useState(0);
  const [finished, setFinished] = useState(false);
  const [floating, setFloating] = useState<Floating[]>([]);
  const [banner, setBanner] = useState<{ text: string; color: string; born: number } | null>(null);
  const [hp, setHp] = useState<Record<string, HpState>>({});
  const [paused, setPaused] = useState(false);
  const [deploymentSeconds, setDeploymentSeconds] = useState(2);
  const [modelError, setModelError] = useState(false);
  const sceneRef = useRef<BattleScene | null>(null);
  const anchorsRef = useRef(new Map<string, HTMLDivElement>());
  const cursor = useRef(0);
  const floatId = useRef(1);
  const tickRef = useRef(0);
  const loadingDeadline = useRef(0);
  const deploymentRemaining = useRef(1.8);
  const report = useMemo(() => {
    const selected = storedReport?.waves?.[wave];
    return storedReport && selected
      ? {
          ...storedReport,
          input: selected.input,
          summary: selected.summary,
          won: selected.summary.winner === "defender",
        }
      : storedReport;
  }, [storedReport, wave]);

  useEffect(() => {
    let active = true;
    setReport(null);
    setError(null);
    setWave(0);
    battlesApi.report(battleId).then(
      (next) => {
        if (active) setReport(next);
      },
      (e: Error) => {
        if (active) setError(e.message);
      },
    );
    return () => {
      active = false;
    };
  }, [battleId]);

  const replay: BattleResult | null = useMemo(
    () => (report ? simulate(report.input as BattleInput) : null),
    [report],
  );
  const hashOk = replay && report ? replay.hash === report.summary.hash : true;

  // Scene lifecycle.
  useEffect(() => {
    if (!handle || !report) return;
    setModelError(false);
    const scene = new BattleScene(handle, () => setModelError(true));
    scene.setReducedMotion(reducedMotion);
    scene.load(report.input as BattleInput, report.details?.visuals);
    scene.takeCamera();
    sceneRef.current = scene;
    loadingDeadline.current = performance.now() + 8000;
    deploymentRemaining.current = 1.8;
    setDeploymentSeconds(2);
    const input = report.input as BattleInput;
    const initial = initialBattleHealth(input);
    setHp(initial);
    cursor.current = 0;
    tickRef.current = 0;
    setTick(0);
    setFinished(false);
    setFloating([]);
    setBanner(null);
    setPaused(false);
    return () => {
      scene.releaseCamera();
      scene.dispose();
      sceneRef.current = null;
    };
  }, [handle, report]);

  useEffect(() => {
    sceneRef.current?.setReducedMotion(reducedMotion);
  }, [reducedMotion]);
  useEffect(() => {
    sceneRef.current?.setPlayback(speed, paused);
  }, [speed, paused, report]);

  // Playback clock: advance ticks, apply events, spawn floating numbers.
  useEffect(() => {
    if (!replay || !report || finished || paused) return;
    let raf = 0;
    let last = performance.now();
    let acc = 0;
    const events = replay.events;
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      const dt = (now - last) / 1000;
      last = now;
      // Give streamed actors time to arrive so the opening abilities are actually visible.
      // A failed/stalled download falls back to the existing position marker and never blocks controls.
      if (!sceneRef.current || (!sceneRef.current.ready && now < loadingDeadline.current)) return;
      // Let the player see the full formation before a strong squad resolves a short encounter.
      if (deploymentRemaining.current > 0) {
        deploymentRemaining.current = Math.max(0, deploymentRemaining.current - Math.min(dt, 0.1));
        setDeploymentSeconds(Math.ceil(deploymentRemaining.current));
        return;
      }
      acc += dt * TICKS_PER_SECOND * speed;
      const whole = Math.floor(acc);
      if (whole <= 0) return;
      acc -= whole;
      const next = Math.min(replay.ticks, tickRef.current + whole);
      const scene = sceneRef.current;
      const newFloats: Floating[] = [];
      const hpPatch: Record<string, HpState> = {};
      while (cursor.current < events.length && events[cursor.current]!.t <= next) {
        const e = events[cursor.current++]!;
        scene?.apply(e);
        applyHp(e, hpPatch);
        const born = (e.t / TICKS_PER_SECOND) * 1000;
        const f = reducedMotion ? null : floatingFor(e, born);
        if (f) newFloats.push({ ...f, id: floatId.current++ });
        if (isUltimate(e)) {
          const actor = e.a ?? "";
          const def = report.summary.units.find((u) => u.id === actor);
          const color =
            def && !def.defId.startsWith("zmb.") && def.defId !== "turret"
              ? tierColor(unitById(def.defId).tier)
              : "#ef4444";
          setBanner({
            text: `${def ? unitName(def.defId) : ""} · ${t("battle.ultimate")}`,
            color,
            born,
          });
        }
      }
      if (Object.keys(hpPatch).length) {
        hpRef.current = { ...hpRef.current, ...hpPatch };
        setHp(hpRef.current);
      }
      const playbackMs = (next / TICKS_PER_SECOND) * 1000;
      setFloating((f) =>
        [...f.filter((x) => playbackMs - x.born < FLOAT_LIFETIME_MS), ...newFloats].slice(-64),
      );
      if (next >= replay.ticks) setFinished(true);
      tickRef.current = next;
      setTick(next);
    };
    const applyHp = (e: BattleEvent, patch: Record<string, HpState>) => {
      const get = (id: string) => patch[id] ?? hpRef.current[id];
      if (e.k === "damage" && e.b) {
        const cur = get(e.b);
        if (cur) patch[e.b] = { ...cur, hp: Math.max(0, cur.hp - (e.v ?? 0)) };
      } else if (e.k === "heal" && e.b) {
        const cur = get(e.b);
        if (cur) patch[e.b] = { ...cur, hp: Math.min(cur.max, cur.hp + (e.v ?? 0)) };
      } else if (e.k === "trap" && e.b) {
        const cur = get(e.b);
        if (cur) patch[e.b] = { ...cur, hp: Math.max(0, cur.hp - (e.v ?? 0)) };
      } else if (e.k === "death" && e.b) {
        const cur = get(e.b);
        if (cur) patch[e.b] = { ...cur, hp: 0, alive: false };
      } else if (e.k === "revive" && e.b) {
        const cur = get(e.b);
        if (cur) patch[e.b] = { ...cur, hp: e.v ?? cur.max * 0.4, alive: true };
      } else if (e.k === "summon" && e.b) {
        patch[e.b] = { hp: 1, max: 1, alive: true };
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [replay, report, speed, finished, paused, t, reducedMotion]);

  const hpRef = useRef(hp);
  hpRef.current = hp;

  // Anchor projection for HP bars and floating numbers.
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const scene = sceneRef.current;
      if (!scene) return;
      for (const a of scene.anchors()) {
        const el = anchorsRef.current.get(a.id);
        if (!el) continue;
        el.style.transform = `translate(-50%, -100%) translate(${a.x.toFixed(1)}px, ${a.y.toFixed(1)}px)`;
        el.style.opacity = a.visible ? "1" : "0";
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [report]);

  if (error)
    return (
      <div className="glass" style={{ margin: 16, padding: 16 }}>
        {error}
      </div>
    );
  if (!report || !replay)
    return (
      <div className="dim" style={{ padding: 16 }}>
        {t("common.loading")}
      </div>
    );
  const input = report.input as BattleInput;
  const members = [
    ...input.attacker.members.map((m) => ({ ...m, side: "attacker" as const })),
    ...input.defender.members.map((m) => ({ ...m, side: "defender" as const })),
  ];
  const skip = () => {
    const scene = sceneRef.current;
    while (cursor.current < replay.events.length)
      scene?.apply(replay.events[cursor.current++]!, { silent: true });
    scene?.settle();
    const finalHp: Record<string, HpState> = { ...hpRef.current };
    for (const u of replay.units)
      if (finalHp[u.id])
        finalHp[u.id] = { ...finalHp[u.id]!, hp: (u.hpPct / 100) * finalHp[u.id]!.max, alive: u.alive };
    setHp(finalHp);
    setTick(replay.ticks);
    tickRef.current = replay.ticks;
    setFloating([]);
    setBanner(null);
    setFinished(true);
  };

  return (
    <div
      className={`battle-view${reducedMotion ? " battle-static" : ""}`}
      data-testid="battle-view"
      data-reduced-motion={reducedMotion}
    >
      {modelError ? (
        <div className="battle-model-error glass" role="status">
          {t("battle.modelsUnavailable")}
        </div>
      ) : null}
      {!finished ? <TacticsPanel input={input} events={replay.events} tick={tick} /> : null}
      <div className="labels">
        {members.map((m) => {
          const h = hp[m.id];
          const name = unitName(m.defId);
          return (
            <div
              key={m.id}
              ref={(el) => {
                if (el) anchorsRef.current.set(m.id, el);
                else anchorsRef.current.delete(m.id);
              }}
              className="label"
              data-testid={`hp-${m.id}`}
            >
              <div
                className={`hp-label ${m.side}`}
                title={name}
                aria-label={`${name} ${h ? Math.round((h.hp / h.max) * 100) : 100}% HP`}
                style={{ opacity: h && !h.alive ? 0.35 : 1 }}
              >
                <div className="ellipsis" style={{ fontSize: 10 }}>
                  {name}
                </div>
                <div className="hp-bar">
                  <div
                    className="hp-fill"
                    style={{ width: `${h ? Math.max(0, Math.min(100, (h.hp / h.max) * 100)) : 100}%` }}
                  />
                </div>
              </div>
              {floating
                .filter((f) => f.actor === m.id)
                .map((f) => (
                  <FloatingNumber
                    key={f.id}
                    born={f.born}
                    time={(tick / TICKS_PER_SECOND) * 1000}
                    color={f.color}
                  >
                    {f.text}
                  </FloatingNumber>
                ))}
            </div>
          );
        })}
      </div>
      {banner ? (
        <UltimateBanner born={banner.born} time={(tick / TICKS_PER_SECOND) * 1000} color={banner.color}>
          {banner.text}
        </UltimateBanner>
      ) : null}
      <div className="battle-hud glass">
        <span className="mono">{(tick / 10).toFixed(1)}s</span>
        <span className="hint">{report.context === "nexus" ? "NEXUS" : report.context}</span>
        {deploymentSeconds > 0 && !finished ? <span className="hint" data-testid="battle-deployment">{pick({ de: `Aufstellung · ${deploymentSeconds}s`, en: `Deployment · ${deploymentSeconds}s` })}</span> : null}
        {storedReport?.waves?.length ? (
          <label className="battle-wave-choice">
            <span>{t("battle.wave")}</span>
            <select
              value={wave}
              onChange={(event) => setWave(Number(event.target.value))}
              data-testid="battle-wave"
              aria-label={t("battle.wave")}
            >
              {storedReport.waves.map((_entry, index) => (
                <option key={index} value={index}>
                  {index + 1} / {storedReport.waves!.length}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {!hashOk ? (
          <span className="badge warn" data-testid="hash-mismatch">
            hash
          </span>
        ) : (
          <span className="badge ok" data-testid="hash-ok">
            <FunctionalIcon name="check" /> {replay.hash.slice(0, 6)}
          </span>
        )}
        <span className="grow" />
        {[1, 2, 4].map((s) => (
          <button
            key={s}
            type="button"
            className={`chip ${speed === s ? "ok" : ""}`}
            onClick={() => setSpeed(s)}
            data-testid={`speed-${s}`}
          >
            {s}×
          </button>
        ))}
        <button
          type="button"
          className="chip"
          onClick={() => setPaused((p) => !p)}
          aria-label={t(paused ? "battle.resume" : "battle.pause")}
          aria-pressed={paused}
          data-testid="battle-pause"
        >
          <FunctionalIcon name={paused ? "play" : "pause"} />
        </button>
        {!finished ? (
          <Button style={{ minHeight: 30 }} onClick={skip} data-testid="battle-skip">
            {t("battle.skip")}
          </Button>
        ) : null}
        <Button
          style={{ minHeight: 30 }}
          onClick={() => navigate(battleReturnRoute(report.context))}
          aria-label={t("common.close")}
          data-testid="battle-back"
        >
          <FunctionalIcon name="close" />
        </Button>
      </div>
      {finished ? (
        <ResultCard
          report={report}
          replay={replay}
          onClose={() => navigate(battleReturnRoute(report.context))}
        />
      ) : null}
    </div>
  );
}

function floatingFor(e: BattleEvent, now: number): Omit<Floating, "id"> | null {
  if (e.k === "damage" && e.b)
    return {
      actor: e.b,
      text: `-${e.v ?? 0}${e.x?.includes("!") ? "!" : ""}`,
      color: e.x?.includes("!") ? "#f5c542" : e.x === "corrode" ? "#84cc16" : "#fca5a5",
      born: now,
    };
  if (e.k === "heal" && e.b) return { actor: e.b, text: `+${e.v ?? 0}`, color: "#86efac", born: now };
  if (e.k === "shield" && e.b) return { actor: e.b, text: <><GameIcon id="emote.shield" size={14} />{e.v ?? 0}</>, color: "#8fd3f4", born: now };
  if (e.k === "miss" && e.b) return { actor: e.b, text: "MISS", color: "#9aa7b5", born: now };
  if (e.k === "trap" && e.b) return { actor: e.b, text: <>-{e.v ?? 0} <FunctionalIcon name="warning" /></>, color: "#f97316", born: now };
  if (e.k === "wall" && e.b) return { actor: e.b, text: <><GameIcon id="bld.bastion_wall" size={14} />{e.v ?? 0}</>, color: "#c0c7d1", born: now };
  return null;
}
